import { AxiosError } from 'axios';
import { retryBackoff } from 'backoff-rxjs';
import { Epic, ofType, StateObservable } from 'redux-observable';
import { defer, EMPTY, from, Observable, of, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AsyncStatus } from '../../../common/enums';
import { ReportJobStatusResponse, ReportStatus, ReportColumnOptionsResponse } from '../../../common/interfaces/report';
import {
    extractImagesFromReportRows,
    transformReportRowsToGridRows,
    transformReportRowToGridRow,
} from '../../../components/Grid/GridDataTransformer';
import * as reportClient from '../../../http-clients/ReportClient';
import { ActionByType, backoffDelayFunction, StoreState, logAndRecordFailureMetrics, logAndRecordSuccessMetrics, AsyncResult } from '../../../store';
import { Actions as ImageActions } from '../../../store/images/Actions';
import { userLocaleSelector } from '../../Auth/Selectors';
import { Actions, ActionTypes } from './Actions';
import { PAGINATION_LIMIT, PAGINATION_LIMIT_FOR_COLUMN_OPTIONS } from './constants';
import { reportColumnsSelector, reportJobSelector } from './Selectors';
import viewClient from '../../../http-clients/ViewClient';
import { loggingClient } from '../../../http-clients/LoggingClient';
import { processContainerIds, handleContainerLinkFailures } from './ReportEpicUtils';
import { PermalinkRequestItem } from '../../../http-clients/interfaces/types';
import { Column } from '@smartsheet/core-views-api-models';

const FILENAME = 'Report/Epic.ts';
const POLLING_DELAY = 1000;
const POLLING_INTERVAL_MS = 500;
const POLLING_MAX_INTERVAL_MS = 120000;
const POLLING_MAX_RETRIES = 20;

/**
 * Epic to initiate the load a report job. Depending on the status of the report job, it will either call the action to fetch the first page of report
 * data or poll the report job until it is ready.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const loadReportJobEpic: Epic<Actions> = (action$: Observable<Actions>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.LOAD_REPORT_JOB),
        switchMap((action: ActionByType<Actions, ActionTypes.LOAD_REPORT_JOB>) => {
            const startTime = Date.now(); // Record the start time

            const { viewId, page } = action.payload;
            return from(reportClient.reportJob(action.payload)).pipe(
                mergeMap((response) => {
                    const actions: Actions[] = [Actions.storeReportJob(response), Actions.setReportDataNotStale()];

                    if (response.statuses.reportStatus === ReportStatus.READY) {
                        // The report is ready, so no need to poll, instead call the action to fetch the report data
                        actions.push(Actions.fetchReportData({ viewId, page }));
                        // Fetch column options since the report job is ready
                        actions.push(Actions.fetchReportColumnOptions({ viewId, reportJobId: response.jobId }));
                    } else {
                        // The report is not ready, this could be due to the complexity of the report, so polling is required
                        actions.push(Actions.pollReportJob({ viewId, page }));
                    }

                    return actions;
                }),
                takeUntil(action$.pipe(ofType(ActionTypes.CANCEL_LOAD_REPORT_JOB))),
                tap(() => {
                    logAndRecordSuccessMetrics({
                        fileName: FILENAME,
                        funcName: 'loadReportJobEpic',
                        message: 'Load report job',
                        startTime,
                        additionalInfo: { viewId },
                    });
                }),
                catchError((error: AxiosError) => {
                    logAndRecordFailureMetrics({ fileName: FILENAME, funcName: 'loadReportJobEpic', startTime, error, additionalInfo: { viewId } });

                    return of(Actions.fetchingErrorReportJob(error));
                })
            );
        })
    );
};

/**
 * Polls the report job status until it is ready.
 *
 * @param {string} viewId - The ID of the view for which the report job status is being polled.
 * @returns {Observable<ReportJobStatusResponse>} - An observable that emits the report job status.
 */
const pollingReportJob = (viewId: string): Observable<ReportJobStatusResponse> => {
    return timer(POLLING_DELAY).pipe(
        switchMap(() => {
            return defer(() => from(reportClient.getReportJobStatus(viewId))).pipe(
                map((response) => {
                    if (response.statuses.reportStatus !== ReportStatus.READY) {
                        // If the report is not ready, throw an error to retry the polling
                        throw new Error(response.statuses.reportStatus);
                    }

                    return response;
                }),
                retryBackoff({
                    initialInterval: POLLING_INTERVAL_MS,
                    maxInterval: POLLING_MAX_INTERVAL_MS,
                    backoffDelay: backoffDelayFunction,
                    resetOnSuccess: true,
                    maxRetries: POLLING_MAX_RETRIES,
                    shouldRetry: (error) => {
                        return error?.message === ReportStatus.PENDING || error?.message === ReportStatus.IN_PROGRESS;
                    },
                })
            );
        })
    );
};

/**
 * Epic to poll the report job status and call the action to fetch the first page of report data when the report job is ready.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const pollReportJobEpic: Epic<Actions> = (action$: Observable<Actions>, state$: StateObservable<StoreState>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.POLL_REPORT_JOB),
        switchMap((action: ActionByType<Actions, ActionTypes.POLL_REPORT_JOB>) => {
            const startTime = Date.now(); // Record the start time

            const { viewId, page } = action.payload;

            return pollingReportJob(viewId).pipe(
                mergeMap(() => {
                    const actions: Actions[] = [Actions.fetchReportData({ viewId, page })];

                    const currentReportJobResult = reportJobSelector(state$.value);
                    if (currentReportJobResult.status === AsyncStatus.DONE) {
                        actions.push(Actions.fetchReportColumnOptions({ viewId, reportJobId: currentReportJobResult.data.jobId }));
                    }

                    return actions;
                }),
                takeUntil(action$.pipe(ofType(ActionTypes.CANCEL_POLL_REPORT_JOB))),
                tap(() => {
                    logAndRecordSuccessMetrics({
                        fileName: FILENAME,
                        funcName: 'pollReportJobEpic',
                        message: 'Poll report job',
                        startTime,
                        additionalInfo: { viewId },
                    });
                }),
                catchError((error: AxiosError) => {
                    logAndRecordFailureMetrics({
                        fileName: FILENAME,
                        funcName: 'pollReportJobEpic',
                        startTime,
                        error,
                        additionalInfo: { viewId },
                    });

                    return of(Actions.fetchingErrorReportData(error));
                })
            );
        })
    );
};

/**
 * Epic to fetch report data.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @param {StateObservable<StoreState>} state$ - Stream of state.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const fetchReportDataEpic: Epic<Actions> = (action$: Observable<Actions>, state$: StateObservable<StoreState>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.FETCH_REPORT_DATA),
        switchMap((action: ActionByType<Actions, ActionTypes.FETCH_REPORT_DATA>) => {
            const { viewId, page, pagination = { limit: PAGINATION_LIMIT }, filter, timestamp } = action.payload;
            // No need to fetch report data if page number is zero. The page number would be zero during the export CSV process. The report data needs
            // to be loaded, but the fetching of the report data happens within the export CSV process.
            if (page === 0) {
                return EMPTY;
            }

            const startTime = Date.now(); // Record the start time
            const reportJobResult = reportJobSelector(state$.value);

            if (reportJobResult.status !== AsyncStatus.DONE) {
                return of(Actions.fetchingErrorReportData(new Error('Report job not complete')));
            }

            return from(
                reportClient.getReport({
                    viewId,
                    pagination,
                    jobId: reportJobResult.data.jobId,
                    filter,
                    timestamp,
                })
            ).pipe(
                mergeMap((response) => {
                    const reportColumnsResult = reportColumnsSelector(state$.value);

                    if (reportColumnsResult.status !== AsyncStatus.DONE) {
                        return of(Actions.fetchingErrorReportData(new Error('Report columns not complete')));
                    }

                    const userLocale = userLocaleSelector(state$.value);
                    const transformedRows = transformReportRowsToGridRows({
                        rows: response.rows,
                        reportColumns: reportColumnsResult.data,
                        userLocale,
                    });

                    const actions: Actions[] = [
                        Actions.storeReportData({ rows: transformedRows, pagination: response.pagination }),
                        Actions.fetchReportContainerLinks(viewId, response.rows),
                        Actions.fetchReportImages(response.rows),
                    ];
                    return actions;
                }),
                takeUntil(action$.pipe(ofType(ActionTypes.CANCEL_FETCH_REPORT_DATA))),
                tap(() => {
                    logAndRecordSuccessMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportDataEpic',
                        message: 'Fetch report data',
                        startTime,
                        page,
                        additionalInfo: { viewId },
                    });
                }),
                catchError((error: AxiosError) => {
                    logAndRecordFailureMetrics({ fileName: FILENAME, funcName: 'fetchReportDataEpic', startTime, error, additionalInfo: { viewId } });

                    return of(Actions.fetchingErrorReportData(error));
                })
            );
        })
    );
};

/**
 * Epic to fetch report columns.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const fetchReportColumnsEpic: Epic<Actions> = (action$: Observable<Actions>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.FETCH_REPORT_COLUMNS),
        switchMap((action: ActionByType<Actions, ActionTypes.FETCH_REPORT_COLUMNS>) => {
            const startTime = Date.now(); // Record the start time
            const viewId = action.payload;
            return from(reportClient.getColumns(viewId)).pipe(
                map((response) => {
                    return Actions.storeReportColumns(response.reportColumns);
                }),
                takeUntil(action$.pipe(ofType(ActionTypes.CANCEL_FETCH_REPORT_COLUMNS))),
                tap(() => {
                    logAndRecordSuccessMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportColumnsEpic',
                        message: 'Fetch report columns',
                        startTime,
                        additionalInfo: { viewId },
                    });
                }),
                catchError((error: AxiosError) => {
                    logAndRecordFailureMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportColumnsEpic',
                        startTime,
                        error,
                        additionalInfo: { viewId },
                    });

                    return of(Actions.fetchingErrorReportColumns(error));
                })
            );
        })
    );
};

/**
 * Epic to fetch container links for report containers.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const fetchReportContainerLinksEpic: Epic<Actions> = (action$: Observable<Actions>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.FETCH_REPORT_CONTAINER_LINKS),
        switchMap((action: ActionByType<Actions, ActionTypes.FETCH_REPORT_CONTAINER_LINKS>) => {
            const { viewId, rows } = action.payload;

            // Process container IDs and create partitions
            const partitions = processContainerIds(rows);

            if (!partitions || partitions.length === 0) {
                return EMPTY;
            }

            // Calculate total number of container IDs from partitions
            const totalContainerIds = partitions.reduce((total: number, partition: PermalinkRequestItem[]) => total + partition.length, 0);

            return from(Promise.all(partitions.map((items: PermalinkRequestItem[]) => viewClient.getContainerLinks(viewId, items)))).pipe(
                map((responses) => {
                    const containerReportLinkMap: Map<number, string> = new Map();
                    responses.forEach((response) => {
                        response.data?.forEach((containerLink) => {
                            containerReportLinkMap.set(containerLink.id, containerLink.permalink);
                        });
                    });
                    return containerReportLinkMap;
                }),
                mergeMap((containerReportLinkMap) => {
                    if (containerReportLinkMap.size === 0) {
                        loggingClient.logError(FILENAME, 'fetchReportContainerLinksEpic', 'Failed to fetch all container links.');
                        return of(Actions.storeReportContainerLinks(viewId, handleContainerLinkFailures(partitions)));
                    }

                    if (containerReportLinkMap.size < totalContainerIds) {
                        loggingClient.logError(
                            FILENAME,
                            'fetchReportContainerLinksEpic',
                            `Partially failed to fetch container links. Got ${containerReportLinkMap.size} of ${totalContainerIds}`
                        );

                        // Handle partial failures by adding '-1' for missing container IDs
                        const updatedContainerReportLinkMap = handleContainerLinkFailures(partitions, containerReportLinkMap);
                        return of(Actions.storeReportContainerLinks(viewId, updatedContainerReportLinkMap));
                    }

                    // All links were successfully fetched
                    return of(Actions.storeReportContainerLinks(viewId, containerReportLinkMap));
                }),
                catchError((error) => {
                    loggingClient.logError(FILENAME, 'fetchReportContainerLinksEpic', error);
                    return of(Actions.storeReportContainerLinks(viewId, handleContainerLinkFailures(partitions)));
                })
            );
        })
    );
};

/**
 * Epic to fetch report row.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @param {StateObservable<StoreState>} state$ - Stream of state.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const fetchReportRowEpic: Epic<Actions> = (action$: Observable<Actions>, state$: StateObservable<StoreState>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.FETCH_REPORT_ROW),
        switchMap((action: ActionByType<Actions, ActionTypes.FETCH_REPORT_ROW>) => {
            const startTime = Date.now(); // Record the start time
            const { viewId, rowId, sheetId, isUpdate } = action.payload;

            return from(reportClient.getReportRows({ viewId, rowIds: [Number(rowId)], sheetId })).pipe(
                map((response) => {
                    const reportColumnsResult = reportColumnsSelector(state$.value);
                    if (reportColumnsResult.status !== AsyncStatus.DONE) {
                        return Actions.fetchingErrorReportData(new Error('Report columns not complete'));
                    }

                    const userLocale = userLocaleSelector(state$.value);

                    const gridRow = transformReportRowToGridRow({
                        row: response.rows[0],
                        reportColumns: reportColumnsResult.data,
                        userLocale,
                    });

                    if (isUpdate) {
                        return Actions.updateReportRow(viewId, gridRow);
                    }
                    return Actions.addReportRow(viewId, gridRow);
                }),
                takeUntil(action$.pipe(ofType(ActionTypes.CANCEL_FETCH_REPORT_ROW))),
                tap(() => {
                    logAndRecordSuccessMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportRowEpic',
                        message: 'Fetch report row',
                        startTime,
                        additionalInfo: { viewId },
                    });
                }),
                catchError((error: AxiosError) => {
                    logAndRecordFailureMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportRowEpic',
                        startTime,
                        error,
                        additionalInfo: { viewId },
                    });

                    return of(Actions.fetchingErrorReportRow(error));
                })
            );
        })
    );
};

/**
 * Epic to fetch report images from report rows.
 *
 * @param action$
 * @returns {Observable<Actions | ImageActions>} - Stream of resulting actions.
 */
export const fetchReportImagesEpic: Epic<Actions | ImageActions> = (
    action$: Observable<Actions | ImageActions>
): Observable<Actions | ImageActions> => {
    return action$.pipe(
        ofType(ActionTypes.FETCH_REPORT_IMAGES),
        switchMap((action: ActionByType<Actions, ActionTypes.FETCH_REPORT_IMAGES>) => {
            const { rows } = action.payload;

            // Extract images from report rows
            const images = extractImagesFromReportRows(rows);

            if (images.length === 0) {
                return EMPTY;
            }

            return of(ImageActions.fetchImageUrls(images));
        })
    );
};

/**
 * Helper function to fetch all report column options with pagination support.
 *
 * @param {string} viewId - The view ID.
 * @param {string} reportJobId - The report job ID.
 * @param {AsyncResult<Column[], AxiosError | Error>} reportColumnsResult - The report columns result.
 * @returns {Observable<ReportColumnOptionsResponse>} - Observable that emits the complete column options response.
 */
const fetchAllReportColumnOptions = (
    viewId: string,
    reportJobId: string,
    reportColumnsResult: AsyncResult<Column[], AxiosError | Error>
): Observable<ReportColumnOptionsResponse> => {
    const allReportColumnOptions: ReportColumnOptionsResponse['reportColumnOptions'] = [];
    const allReportContactColumnOptions: ReportColumnOptionsResponse['reportContactColumnOptions'] = [];
    let traceId = '';

    const fetchPage = (nextCursor?: string): Observable<ReportColumnOptionsResponse> => {
        const requestParams = {
            reportJobId,
            reportColumnsResult,
            pagination: { limit: PAGINATION_LIMIT_FOR_COLUMN_OPTIONS, nextCursor },
        };

        return from(reportClient.getReportColumnOptions(viewId, requestParams)).pipe(
            mergeMap((response: ReportColumnOptionsResponse) => {
                // Accumulate the results
                allReportColumnOptions.push(...response.reportColumnOptions);
                allReportContactColumnOptions.push(...response.reportContactColumnOptions);
                traceId = response.traceId;

                // Check if there are more pages using nextCursor
                if (response.pagination.nextCursor) {
                    return fetchPage(response.pagination.nextCursor);
                } else {
                    // Return the complete aggregated response
                    return of({
                        traceId,
                        pagination: { limit: allReportColumnOptions.length + allReportContactColumnOptions.length },
                        reportColumnOptions: allReportColumnOptions,
                        reportContactColumnOptions: allReportContactColumnOptions,
                    });
                }
            })
        );
    };

    return fetchPage();
};

/**
 * Epic to fetch report column options and contact column options.
 * This epic fetches ALL column options using pagination to ensure no options are missed.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @param {StateObservable<StoreState>} state$ - Stream of state.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const fetchReportColumnOptionsEpic: Epic<Actions> = (
    action$: Observable<Actions>,
    state$: StateObservable<StoreState>
): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.FETCH_REPORT_COLUMN_OPTIONS),
        switchMap((action: ActionByType<Actions, ActionTypes.FETCH_REPORT_COLUMN_OPTIONS>) => {
            const startTime = Date.now();
            const { viewId, reportJobId } = action.payload;

            const reportColumnsResult = reportColumnsSelector(state$.value);
            if (reportColumnsResult.status !== AsyncStatus.DONE) {
                return of(Actions.fetchingErrorReportColumnOptions(new Error('Report columns not complete')));
            }

            return fetchAllReportColumnOptions(viewId, reportJobId, reportColumnsResult).pipe(
                map((response) => {
                    return Actions.storeReportColumnOptions(response);
                }),
                takeUntil(action$.pipe(ofType(ActionTypes.CANCEL_FETCH_REPORT_COLUMN_OPTIONS))),
                tap(() => {
                    logAndRecordSuccessMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportColumnOptionsEpic',
                        message: 'Fetch report column options',
                        startTime,
                        additionalInfo: { viewId, reportJobId },
                    });
                }),
                catchError((error: AxiosError) => {
                    logAndRecordFailureMetrics({
                        fileName: FILENAME,
                        funcName: 'fetchReportColumnOptionsEpic',
                        startTime,
                        error,
                        additionalInfo: { viewId, reportJobId },
                    });

                    return of(Actions.fetchingErrorReportColumnOptions(error));
                })
            );
        })
    );
};

/**
 * Epic to reset the report state by canceling all ongoing report-related actions.
 *
 * @param {Observable<Actions>} action$ - Stream of actions.
 * @returns {Observable<Actions>} - Stream of resulting actions.
 */
export const resetReportEpic: Epic<Actions> = (action$: Observable<Actions>): Observable<Actions> => {
    return action$.pipe(
        ofType(ActionTypes.RESET_REPORT_STATE),
        mergeMap(() => {
            return [
                Actions.cancelLoadReportJob(),
                Actions.cancelPollReportJob(),
                Actions.cancelFetchReportData(),
                Actions.cancelFetchReportColumns(),
                Actions.cancelFetchReportContainerLinks(),
                Actions.cancelFetchReportRow(),
                Actions.cancelFetchReportColumnOptions(),
            ];
        })
    );
};
